import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Star, Award, ShieldCheck, ArrowRight, MapPin } from 'lucide-react';
import { LOGO, JESSICA_PHOTO, PHONE_NUMBER, PHONE_TEL_HREF } from '../../constants/site';

const TRUST = [
  { icon: Star, label: '5-Star Rated', sub: 'Google & Zillow reviews' },
  { icon: Award, label: '500+ Homes Sold', sub: 'Coastal VA & NC' },
  { icon: ShieldCheck, label: 'Swell Realty Co.', sub: 'Luxury marketing & reach' },
];

export const scrollToForm = () => document.getElementById('home-value-form')?.scrollIntoView({ behavior: 'smooth' });

export const SellerHeader = () => (
  <header className="bg-[#143160] text-white">
    <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
      <Link to="/" data-testid="seller-home-link"><img src={LOGO} alt="Jess Sells East Coast" className="h-12 w-auto brightness-0 invert" /></Link>
      <a href={PHONE_TEL_HREF} data-testid="seller-call-top" className="flex items-center space-x-2 font-bold text-gold-300 hover:text-gold-200"><Phone className="w-4 h-4" /><span>{PHONE_NUMBER}</span></a>
    </div>
  </header>
);

const Headline = ({ area }) => {
  const h1 = area.custom?.h1;
  if (!h1) return <>What's Your <span className="gold-gradient-text font-black">{area.name}</span> Home Worth?</>;
  return <>{h1.before && `${h1.before} `}<span className="gold-gradient-text font-black">{h1.accent}</span>{h1.after && ` ${h1.after}`}</>;
};

const TrustStrip = () => (
  <div className="grid grid-cols-3 gap-4 pt-4">
    {TRUST.map(({ icon: Icon, label, sub }) => (
      <div key={label} className="flex items-start gap-2">
        <Icon className="w-5 h-5 text-gold-400 mt-0.5 shrink-0" />
        <div><p className="font-bold text-sm leading-tight">{label}</p><p className="text-xs text-white/60">{sub}</p></div>
      </div>
    ))}
  </div>
);

const Portrait = ({ areaName }) => (
  <div className="relative mx-auto w-full max-w-sm">
    <div className="absolute -inset-4 bg-gold-500/20 blur-3xl rounded-3xl"></div>
    <div className="relative rounded-2xl overflow-hidden border-2 border-gold-400/70 shadow-2xl aspect-[3/4]">
      <img src={JESSICA_PHOTO} alt={`Jessica Burnette, REALTOR® – ${areaName} home values and listing specialist`} className="w-full h-full object-cover object-top" fetchPriority="high" />
    </div>
    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-sand-50 text-[#143160] px-5 py-2.5 rounded-full shadow-xl text-sm font-bold whitespace-nowrap">Jessica Burnette · REALTOR® · MRP®</div>
  </div>
);

export const SellerHero = ({ area }) => (
  <section className="bg-[#143160] text-white pb-16 pt-6 sm:pt-12 relative overflow-hidden">
    <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_30%_20%,#BFA26A,transparent_45%)]"></div>
    <div className="relative max-w-6xl mx-auto px-5 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
      <div className="space-y-6">
        <p className="text-gold-300 font-bold tracking-[0.25em] text-xs uppercase flex items-center gap-2"><MapPin className="w-4 h-4 shrink-0" />{area.custom?.eyebrow || `${area.region} Sellers`}</p>
        <h1 data-testid="seller-h1" className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"><Headline area={area} /></h1>
        <p className="text-lg sm:text-xl text-white/85 leading-relaxed max-w-xl">{area.custom?.intro || `${area.name} home values have shifted this year. Get a free, no-pressure home value report and see where you stand.`} <span className="text-gold-300 font-semibold">No strings, just the number.</span></p>
        <div className="h-px w-40 bg-gold-500"></div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button type="button" onClick={scrollToForm} data-testid="seller-cta-report" className="inline-flex items-center justify-center bg-gold-500 hover:bg-gold-400 text-[#143160] font-black px-7 py-4 rounded-md text-base uppercase tracking-wide transition-colors">Get My Free Report <ArrowRight className="w-5 h-5 ml-2" /></button>
          <a href={PHONE_TEL_HREF} data-testid="seller-cta-call" className="inline-flex items-center justify-center border-2 border-white/40 hover:border-gold-400 text-white font-bold px-7 py-4 rounded-md text-base transition-colors"><Phone className="w-5 h-5 mr-2 text-gold-300" />Call or text {PHONE_NUMBER}</a>
        </div>
        <TrustStrip />
      </div>
      <Portrait areaName={area.name} />
    </div>
  </section>
);
